import React, { useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import Modal from '../../../../../Modal';
import Table from '../../../../../Table';
import TransactionAddOrEditModal from './TransactionAddOrEditModal';
import ExclusionReason from './ExclusionReason';

import { useUiStore } from '../../../../../../store';
import useUserRole from '../../../../../../hooks/useUserRole';
import BankStatementTransactionService from '../../../../../../services/BankStatementTransactionService';

export default function TransactionsTableModal({
  isVisible,
  hideModal,
  transactions,
  refetch,
  bankStatementId,
}) {
  const [
    isTransactionAddOrEditModalVisible,
    setIsTransactionAddOrEditModalVisible,
  ] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(null);

  const showDialogModal = useUiStore((state) => state.showDialogModal);
  const notifySuccess = useUiStore((state) => state.notifySuccess);
  const notifyApiError = useUiStore((state) => state.notifyApiError);

  const { isOwner, isSuperAdmin } = useUserRole();

  const { mutate: deleteTransaction } = useMutation({
    mutationFn: (transactionId) =>
      BankStatementTransactionService.deleteBankStatementTransaction(
        transactionId
      ),
    onSuccess: async () => {
      await refetch();
      notifySuccess('Transaction deleted successfully');
    },
    onError: (error) => {
      console.log(error);
      notifyApiError('Error deleting transaction');
    },
  });

  const { mutate: updateTransaction } = useMutation({
    mutationFn: (transaction) =>
      BankStatementTransactionService.updateBankStatementTransaction(
        transaction
      ),
    onSuccess: async () => {
      refetch();
      notifySuccess('Transaction updated successfully');
    },
    onError: (error) => {
      console.log(error);
      notifyApiError('Error updating transaction');
    },
  });

  const cols = [
    {
      title: 'Date',
      key: 'date',
      type: 'date',
    },
    {
      title: 'Description',
      key: 'description',
    },
    {
      title: 'Deposit Amount',
      key: 'depositAmount',
      type: 'currency',
    },
    {
      title: 'Deduction Amount',
      key: 'deductionAmount',
      type: 'currency',
    },
    {
      title: 'Excluded',
      key: 'isExcluded',
      type: 'checkbox',
      readOnly: !isOwner && !isSuperAdmin,
      onChange: (value, row) => {
        updateTransaction({
          formValues: { isExcluded: value },
          id: row.id,
        });
      },
    },
    {
      title: 'Exclusion Reason',
      key: 'exclusionReason',
      type: 'component',
      component: ExclusionReason,
      props: (row) => {
        return {
          transactionId: row.id,
          value: row.exclusionReason,
          isEditable: row.isExcluded,
          updateTransaction,
        };
      },
    },
  ];

  const actions = [
    {
      title: 'Edit',
      onClick: (row) => {
        setCurrentTransaction(row);
        setIsTransactionAddOrEditModalVisible(true);
      },
      icon: 'edit',
    },
    {
      title: 'Delete',
      onClick: (row) =>
        showDialogModal({
          title: 'Delete Document',
          text: `Are you sure you want to delete this document?`,
          onConfirm: () => deleteTransaction(row.id),
        }),
      icon: 'delete',
      danger: true,
    },
  ];

  return (
    <>
      <Modal isVisible={isVisible} hideModal={hideModal} title="Transactions">
        <div>
          <Table
            createButtonLabel="Add Transaction"
            onCreateButtonClick={() => {
              setCurrentTransaction(null);
              setIsTransactionAddOrEditModalVisible(true);
            }}
            cols={cols}
            rows={transactions}
            actions={isOwner || isSuperAdmin ? actions : []}
            countName="Transactions"
            style={{
              maxHeight: '80vh',
            }}
          />
        </div>
      </Modal>
      <TransactionAddOrEditModal
        isVisible={isTransactionAddOrEditModalVisible}
        hideModal={() => setIsTransactionAddOrEditModalVisible(false)}
        transaction={currentTransaction}
        refetch={refetch}
        bankStatementId={bankStatementId}
      />
    </>
  );
}
